import {
  useMemo,
  useCallback,
  createContext,
  useState,
  useContext,
  PropsWithChildren,
} from 'react';
import ExternalPropertyFilter from 'crono-fe-common/types/externalPropertyFilter';
import { StrategySearchSort } from 'crono-fe-common/types/DTO/strategySearch';
import { externalPropertiesToTagInsert } from 'crono-fe-common/utils';
import { useAuth } from 'context/auth';
import { StrategyFilters } from 'crono-fe-common/types/strategyFilters';
import usePatchUserPreferences from 'hooks/services/user/usePatchUserPreferences';
import {
  createSearchParams,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import PATH from 'crono-fe-common/constants/path';
import { STRATEGY_PARAM_KEYS } from 'context/strategyDetail/navigationConstants';
import {
  legacyUserToUserIds,
  appliedUsersFiltersCount,
  userIdsToLegacyUser,
} from 'hooks/useUsersFilter';

// Parse saved filters from JSON string with error handling
const parseStrategyFilters = (
  filtersString: string | null | undefined,
): StrategyFilters | null => {
  if (!filtersString) return null;
  try {
    return JSON.parse(filtersString) as StrategyFilters;
  } catch (error) {
    console.warn(
      'Failed to parse strategy filters from user preferences:',
      error,
    );
    return null;
  }
};

// Reusable hook for saving strategy filters to user preferences
const useSaveStrategyFilters = () => {
  const { user } = useAuth();
  const { mutate: patchUserPreferences } = usePatchUserPreferences();

  return useCallback(
    (updates: Partial<StrategyFilters>) => {
      const currentFilters =
        parseStrategyFilters(user?.userPreferences?.strategyFilters) || {};

      const newFilters: StrategyFilters = {
        ...currentFilters,
        ...updates,
      };

      const serializedFilters = JSON.stringify(newFilters);

      if (user?.userPreferences?.strategyFilters === serializedFilters) {
        return;
      }

      patchUserPreferences({ strategyFilters: serializedFilters });

      if (user) {
        if (user.userPreferences) {
          user.userPreferences.strategyFilters = serializedFilters;
        } else {
          user.userPreferences = { strategyFilters: serializedFilters };
        }
      }
    },
    [user, patchUserPreferences],
  );
};

interface StrategyFiltersContextType {
  sortOption: StrategySearchSort;
  setSortOption: (sort: StrategySearchSort) => void;
  selectedUser: number | undefined;
  setSelectedUser: (userId: number | undefined) => void;
  filterExternalProperties: ExternalPropertyFilter[];
  setFilterExternalProperties: (
    externalProperties: ExternalPropertyFilter[],
  ) => void;
}

const StrategyFiltersContext = createContext<
  StrategyFiltersContextType | undefined
>(undefined);

export const StrategyFiltersProvider = ({ children }: PropsWithChildren) => {
  const { user } = useAuth();
  const saveStrategyFilters = useSaveStrategyFilters();

  const savedFilters = parseStrategyFilters(
    user?.userPreferences?.strategyFilters,
  );

  const [sortOption, setSortOption] = useState<StrategySearchSort>(
    savedFilters?.sort ?? 'CreatedDesc',
  );
  const [selectedUser, setSelectedUser] = useState<number | undefined>(
    savedFilters?.selectedUser ?? undefined,
  );
  const [filterExternalProperties, setFilterExternalProperties] = useState<
    ExternalPropertyFilter[]
  >(savedFilters?.externalProperties ?? []);

  return (
    <StrategyFiltersContext.Provider
      value={{
        sortOption,
        setSortOption: (value) => {
          setSortOption(value);
          saveStrategyFilters({ sort: value });
        },
        selectedUser,
        setSelectedUser: (value) => {
          setSelectedUser(value);
          saveStrategyFilters({ selectedUser: value });
        },
        filterExternalProperties,
        setFilterExternalProperties: (value) => {
          setFilterExternalProperties(value);
          saveStrategyFilters({ externalProperties: value });
        },
      }}
    >
      {children}
    </StrategyFiltersContext.Provider>
  );
};

export const useStrategyFilters = () => {
  const context = useContext(StrategyFiltersContext);

  if (!context) {
    throw new Error(
      'useStrategyFilters must be used within a StrategyFiltersProvider',
    );
  }

  return context;
};

export const useStrategyPageFilters = () => {
  const { user } = useAuth();
  const saveStrategyFiltersToPreferences = useSaveStrategyFilters();

  const [searchParams] = useSearchParams();
  const searchParamsFilters = searchParams.get(STRATEGY_PARAM_KEYS.filters);

  // Load initial values from user preferences or use defaults
  const savedFilters = {
    ...parseStrategyFilters(user?.userPreferences?.strategyFilters),
    ...(searchParamsFilters ? parseStrategyFilters(searchParamsFilters) : {}),
  };

  const sortOption = savedFilters.sort ?? 'CreatedDesc';
  // Migrate legacy single-owner preferences to the multi-owner arrays.
  const userIds =
    savedFilters?.userIds ?? legacyUserToUserIds(savedFilters?.selectedUser);
  const teamIds = savedFilters?.userListIds ?? [];

  const filterExternalProperties = savedFilters?.externalProperties ?? [];
  const search = searchParams.get(STRATEGY_PARAM_KEYS.search) ?? '';

  const navigate = useNavigate();

  const saveStrategyFilters = useCallback(
    (updates: Partial<StrategyFilters> & { search?: string }) => {
      const currentFilters =
        parseStrategyFilters(user?.userPreferences?.strategyFilters) || {};
      const newFilters: StrategyFilters = {
        ...currentFilters,
        ...updates,
      };

      const serializedFilters = JSON.stringify(newFilters);

      navigate({
        pathname: PATH.STRATEGIES,
        search: createSearchParams({
          filters: serializedFilters,
          ...(updates.search ? { search: updates.search } : {}),
        }).toString(),
      });

      saveStrategyFiltersToPreferences(updates);
    },
    [user],
  );

  const setFilterExternalProperties = (value: ExternalPropertyFilter[]) => {
    saveStrategyFilters({ externalProperties: value, search });
  };

  const setSortOption = (sort: StrategySearchSort) => {
    saveStrategyFilters({ sort, search });
  };

  const setSelectedUsers = (userIds: number[], teamIds: number[]) => {
    saveStrategyFilters({
      userIds,
      userListIds: teamIds,
      // Keep the legacy single-owner field synced so the strategy pickers
      // (which still read selectedUser for their "my sequences" toggle) stay
      // consistent.
      selectedUser: userIdsToLegacyUser(userIds, teamIds),
      search,
    });
  };

  const setSearch = useCallback(
    (search?: string) => {
      const newSearchParams = createSearchParams(searchParams);
      if (search) {
        newSearchParams.set(STRATEGY_PARAM_KEYS.search, search);
      } else {
        newSearchParams.delete(STRATEGY_PARAM_KEYS.search);
      }

      navigate({
        pathname: PATH.STRATEGIES,
        search: newSearchParams.toString(),
      });
    },
    [searchParams, navigate],
  );

  const flatTagsFilter = useMemo(() => {
    return externalPropertiesToTagInsert(filterExternalProperties);
  }, [filterExternalProperties]);

  // Not memoized: userIds/teamIds are rebuilt from savedFilters every render, so
  // a useMemo here would recompute every render anyway. The count is cheap.
  let numberOfAppliedFilters = appliedUsersFiltersCount(
    userIds,
    teamIds,
    false,
  );
  if (filterExternalProperties.length > 0) {
    numberOfAppliedFilters += filterExternalProperties.reduce(
      (sum, filter) => sum + (filter.values?.length || 0),
      0,
    );
  }

  const resetFilters = () => {
    const defaultFilters: StrategyFilters = {
      sort: 'CreatedDesc',
      selectedUser: undefined,
      userIds: [],
      userListIds: [],
      externalProperties: [],
    };

    saveStrategyFilters({ ...defaultFilters, search });
  };

  const applyFilters = (
    sort: StrategySearchSort,
    userIds: number[],
    teamIds: number[],
    externalProperties: ExternalPropertyFilter[],
    search?: string,
  ) => {
    saveStrategyFilters({
      sort,
      userIds,
      userListIds: teamIds,
      selectedUser: userIdsToLegacyUser(userIds, teamIds),
      externalProperties,
      search,
    });
  };

  return {
    sortOption,
    setSortOption,
    userIds,
    teamIds,
    setSelectedUsers,
    filterExternalProperties,
    setFilterExternalProperties,
    flatTagsFilter,
    numberOfAppliedFilters,
    resetFilters,
    applyFilters,
    search,
    setSearch,
  };
};
